import { BudgetOnboardingTransactionsSyncing } from '@/components/cashflow/budget/BudgetOnboardingTransactionsSyncing';
import Modal from '@/components/common/modals/Modal';
import ModalManager, { useModal } from '@/components/ModalManager';
import {
    useTracking,
    useTrackModalShown,
} from '@/services/analytics/useTracking';
import { BudgetFormVariant, BudgetOnboardingStep } from '@common/react/budget';
import { useEffect } from 'react';
import { BudgetOnboardingConfirm } from './BudgetOnboardingConfirm';
import {
    BudgetOnboardingProvider,
    useBudgetOnboardingContext,
} from './BudgetOnboardingContext';
import { BudgetOnboardingFixedExpenses } from './BudgetOnboardingFixedExpenses';
import { BudgetOnboardingIncome } from './BudgetOnboardingIncome';
import { BudgetOnboardingLifestyleExpenses } from './BudgetOnboardingLifestyleExpenses';
import { BudgetOnboardingLinkAccounts } from './BudgetOnboardingLinkAccounts';
import { BudgetOnboardingSavingInvesting } from './BudgetOnboardingSavingInvesting';

interface BudgetOnboardingModalProps {
    initialStep?: BudgetOnboardingStep;
    variant: BudgetFormVariant;
    autofocusGroupId?: string;
}

export const showBudgetOnboardingModal = (props: BudgetOnboardingModalProps) =>
    ModalManager.show(BudgetOnboardingModal, props);

export const BudgetOnboardingModal =
    ModalManager.create<BudgetOnboardingModalProps>(
        ({ variant, initialStep, autofocusGroupId }) => {
            useTrackModalShown('Budget Onboarding');

            const modal = useModal();

            return (
                <Modal
                    size="3xl"
                    isOpen={modal.visible}
                    onClose={modal.remove}
                    scrollBehavior="inside"
                    isCentered={true}
                    isFullScreen
                    closeOnOverlayClick={false}
                    unstyled={true}
                >
                    <BudgetOnboardingProvider
                        variant={variant}
                        initialStep={initialStep}
                        autofocusGroupId={autofocusGroupId}
                    >
                        <BudgetOnboardingModalInner />
                    </BudgetOnboardingProvider>
                </Modal>
            );
        }
    );

const BudgetOnboardingModalInner = () => {
    const { track } = useTracking();

    const { currentStep } = useBudgetOnboardingContext();

    useEffect(() => {
        track('Budget Onboarding Step Viewed', { step: currentStep });
    }, [track, currentStep]);

    const Component = {
        'link-accounts': BudgetOnboardingLinkAccounts,
        income: BudgetOnboardingIncome,
        'transactions-syncing': BudgetOnboardingTransactionsSyncing,
        'fixed-expenses': BudgetOnboardingFixedExpenses,
        'lifestyle-expenses': BudgetOnboardingLifestyleExpenses,
        'saving-investing': BudgetOnboardingSavingInvesting,
        confirmation: BudgetOnboardingConfirm,
    }[currentStep];

    return <Component />;
};
